import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Ticket, Sparkles, Percent, Calendar, Hash, ToggleLeft, ToggleRight } from "lucide-react";
import { Link } from "react-router-dom";
import { adminCouponsApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

export default function AdminCouponNewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const isEditMode = !!id;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percentage" as "percentage" | "fixed",
    discount_value: 0,
    min_order_amount: "",
    max_discount: "",
    usage_limit: "",
    valid_from: "",
    valid_until: "",
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    if (isEditMode && token) {
      fetchCoupon();
    } else {
      // Set default valid_from to today
      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({ ...prev, valid_from: today }));
    }
  }, [id, token, isEditMode]);

  const fetchCoupon = async () => {
    if (!token || !id) return;
    try {
      setLoading(true);
      const coupon = await adminCouponsApi.getCoupon(parseInt(id), token);
      setFormData({
        code: coupon.code || "",
        description: coupon.description || "",
        discount_type: coupon.discount_type || "percentage",
        discount_value: coupon.discount_value || 0,
        min_order_amount: coupon.min_order_amount?.toString() || "",
        max_discount: coupon.max_discount?.toString() || "",
        usage_limit: coupon.usage_limit?.toString() || "",
        valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().split("T")[0] : "",
        valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().split("T")[0] : "",
        is_active: coupon.is_active ?? true,
      });
    } catch (error: any) {
      console.error("Error fetching coupon:", error);
      toast.error(error.response?.data?.detail || "Failed to load coupon");
      navigate("/admin/coupons");
    } finally {
      setLoading(false);
    }
  };

  const generateCouponCode = () => {
    setGeneratingCode(true);
    // Generate a random coupon code
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, code }));
    setGeneratingCode(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = "Coupon code is required";
    }

    if (!formData.discount_type) {
      newErrors.discount_type = "Discount type is required";
    }

    if (!formData.discount_value || formData.discount_value <= 0) {
      newErrors.discount_value = "Discount value must be greater than 0";
    }

    if (formData.discount_type === "percentage") {
      if (formData.discount_value > 100) {
        newErrors.discount_value = "Percentage discount cannot exceed 100%";
      }
    }

    if (formData.min_order_amount && parseFloat(formData.min_order_amount) < 0) {
      newErrors.min_order_amount = "Minimum order amount cannot be negative";
    }

    if (formData.max_discount && parseFloat(formData.max_discount) < 0) {
      newErrors.max_discount = "Maximum discount cannot be negative";
    }

    if (formData.usage_limit && parseInt(formData.usage_limit) < 0) {
      newErrors.usage_limit = "Usage limit cannot be negative";
    }

    if (!formData.valid_from) {
      newErrors.valid_from = "Valid from date is required";
    }

    if (formData.valid_until && formData.valid_from) {
      const fromDate = new Date(formData.valid_from);
      const untilDate = new Date(formData.valid_until);
      if (untilDate < fromDate) {
        newErrors.valid_until = "Valid until date must be after valid from date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !token) return;

    setLoading(true);
    try {
      const couponData: any = {
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        is_active: formData.is_active,
      };

      if (isEditMode && id) {
        await adminCouponsApi.updateCoupon(parseInt(id), couponData, token);
        toast.success("Coupon updated successfully");
      } else {
        await adminCouponsApi.createCoupon(couponData, token);
        toast.success("Coupon created successfully");
      }

      navigate("/admin/coupons");
    } catch (error: any) {
      console.error("Error saving coupon:", error);
      toast.error(error.response?.data?.detail || "Failed to save coupon");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-2" />
          <div className="h-4 w-48 bg-gray-200 rounded-lg animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/coupons"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Coupons
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">
          {isEditMode ? "Edit Coupon" : "Add New Coupon"}
        </h1>
        <p className="text-xs text-gray-500">
          {isEditMode ? "Update coupon details" : "Create a new discount coupon"}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Ticket className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Basic Information</h2>
              </div>
            </div>
            <div className="p-4 space-y-2.5">
              {/* Coupon Code */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Coupon Code <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent uppercase ${
                      errors.code ? "border-red-500" : "border-gray-200"
                    }`}
                    placeholder="COUPON123"
                    disabled={isEditMode}
                  />
                  {!isEditMode && (
                    <button
                      type="button"
                      onClick={generateCouponCode}
                      disabled={generatingCode}
                      className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {generatingCode ? "Generating..." : "Generate"}
                    </button>
                  )}
                </div>
                {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
                <p className="text-xs text-gray-500 mt-1">Code will be automatically converted to uppercase</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                  placeholder="Enter coupon description (optional)"
                />
              </div>
            </div>
          </motion.div>

          {/* Row 1: Discount Details + Usage Settings */}
          <div className="grid grid-cols-2 gap-3">
            {/* Discount Details */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Percent className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Discount Details</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="discount_type"
                    value={formData.discount_type}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                      errors.discount_type ? "border-red-500" : "border-gray-200"
                    }`}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                  {errors.discount_type && (
                    <p className="text-xs text-red-500 mt-1">{errors.discount_type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Discount Value <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      {formData.discount_type === "percentage" ? "%" : "₹"}
                    </span>
                    <input
                      type="number"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleInputChange}
                      min="0"
                      max={formData.discount_type === "percentage" ? "100" : undefined}
                      step="0.01"
                      className={`w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                        errors.discount_value ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder={formData.discount_type === "percentage" ? "10" : "100"}
                    />
                  </div>
                  {errors.discount_value && (
                    <p className="text-xs text-red-500 mt-1">{errors.discount_value}</p>
                  )}
                </div>

                {formData.discount_type === "percentage" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Maximum Discount (₹)
                    </label>
                    <input
                      type="number"
                      name="max_discount"
                      value={formData.max_discount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                        errors.max_discount ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Optional"
                    />
                    {errors.max_discount && (
                      <p className="text-xs text-red-500 mt-1">{errors.max_discount}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Limit the maximum discount amount
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Usage Settings */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <Hash className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Usage Settings</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Minimum Order Amount (₹)
                  </label>
                  <input
                    type="number"
                    name="min_order_amount"
                    value={formData.min_order_amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                      errors.min_order_amount ? "border-red-500" : "border-gray-200"
                    }`}
                    placeholder="Optional"
                  />
                  {errors.min_order_amount && (
                    <p className="text-xs text-red-500 mt-1">{errors.min_order_amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    name="usage_limit"
                    value={formData.usage_limit}
                    onChange={handleInputChange}
                    min="0"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                      errors.usage_limit ? "border-red-500" : "border-gray-200"
                    }`}
                    placeholder="Optional"
                  />
                  {errors.usage_limit && (
                    <p className="text-xs text-red-500 mt-1">{errors.usage_limit}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty for unlimited usage
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Validity & Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Validity & Status</h2>
              </div>
            </div>
            <div className="p-4 space-y-2.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                      errors.valid_from ? "border-red-500" : "border-gray-200"
                    }`}
                  />
                  {errors.valid_from && (
                    <p className="text-xs text-red-500 mt-1">{errors.valid_from}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleInputChange}
                    min={formData.valid_from}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent ${
                      errors.valid_until ? "border-red-500" : "border-gray-200"
                    }`}
                  />
                  {errors.valid_until && (
                    <p className="text-xs text-red-500 mt-1">{errors.valid_until}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-0.5">
                    Active Status
                  </label>
                  <p className="text-xs text-gray-500">
                    {formData.is_active
                      ? "Coupon is currently active and can be used"
                      : "Coupon is inactive and cannot be used"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, is_active: !formData.is_active })
                  }
                  className="flex items-center gap-2"
                >
                  {formData.is_active ? (
                    <ToggleRight className="w-6 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <Link
            to="/admin/coupons"
            className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {loading ? "Saving..." : isEditMode ? "Update Coupon" : "Create Coupon"}
          </button>
        </div>
      </form>
    </div>
  );
}

